<template>
  <div class="mental-test-container">
    <el-card class="test-card">
      <template #header>
        <div class="card-header">
          <h2>心理健康测试</h2>
          <el-steps :active="currentStep" finish-status="success" simple>
            <el-step title="基本信息" />
            <el-step title="测试问题" />
            <el-step title="测试结果" />
          </el-steps>
        </div>
      </template>

      <!-- 基本信息步骤 -->
      <div v-if="currentStep === 0" class="step-content">
        <el-form :model="basicInfo" :rules="basicRules" ref="basicInfoForm" label-width="100px">
          <el-form-item label="姓名" prop="name">
            <el-input v-model="basicInfo.name" placeholder="请输入您的姓名"></el-input>
          </el-form-item>
          <el-form-item label="年龄" prop="age">
            <el-input-number v-model="basicInfo.age" :min="18" :max="100"></el-input-number>
          </el-form-item>
          <el-form-item label="性别" prop="gender">
            <el-radio-group v-model="basicInfo.gender">
              <el-radio label="male">男</el-radio>
              <el-radio label="female">女</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="nextStep">开始测试</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 测试问题步骤 -->
      <div v-if="currentStep === 1" class="step-content">
        <div class="question-container">
          <h3>问题 {{ currentQuestion + 1 }}/{{ questions.length }}</h3>
          <p class="question-text">{{ questions[currentQuestion].text }}</p>
          <el-radio-group v-model="answers[currentQuestion]" class="options-group">
            <el-radio 
              v-for="(option, index) in questions[currentQuestion].options" 
              :key="index" 
              :label="option.value"
              class="option-item"
            >
              {{ option.text }}
            </el-radio>
          </el-radio-group>
          <div class="navigation-buttons">
            <el-button @click="prevQuestion" :disabled="currentQuestion === 0">上一题</el-button>
            <el-button 
              type="primary" 
              @click="nextQuestion"
              :disabled="!answers[currentQuestion]"
            >
              {{ currentQuestion === questions.length - 1 ? '完成测试' : '下一题' }}
            </el-button>
          </div>
        </div>
      </div>

      <!-- 测试结果步骤 -->
      <div v-if="currentStep === 2" class="step-content">
        <div class="result-container">
          <h3>测试结果</h3>
          <el-result
            :icon="resultIcon"
            :title="resultTitle"
            :sub-title="resultSubtitle"
          >
            <template #extra>
              <div class="result-details">
                <p>{{ resultDescription }}</p>
                <div class="suggestion-list">
                  <h4>建议：</h4>
                  <ul>
                    <li v-for="(suggestion, index) in suggestions" :key="index">
                      {{ suggestion }}
                    </li>
                  </ul>
                </div>
              </div>
              <div class="action-buttons">
                <el-button type="primary" @click="restartTest">重新测试</el-button>
                <el-button @click="downloadReport">下载报告</el-button>
              </div>
            </template>
          </el-result>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'MentalTestView',
  data() {
    return {
      currentStep: 0,
      currentQuestion: 0,
      basicInfo: {
        name: '',
        age: 18,
        gender: ''
      },
      basicRules: {
        name: [
          { required: true, message: '请输入姓名', trigger: 'blur' }
        ],
        age: [
          { required: true, message: '请输入年龄', trigger: 'blur' }
        ],
        gender: [
          { required: true, message: '请选择性别', trigger: 'change' }
        ]
      },
      questions: [
        {
          text: '在过去的两周里，您是否经常感到情绪低落、沮丧或绝望？',
          options: [
            { text: '完全没有', value: 0 },
            { text: '有几天', value: 1 },
            { text: '一半以上的时间', value: 2 },
            { text: '几乎每天', value: 3 }
          ]
        },
        {
          text: '您是否对日常活动失去了兴趣或乐趣？',
          options: [
            { text: '完全没有', value: 0 },
            { text: '有几天', value: 1 },
            { text: '一半以上的时间', value: 2 },
            { text: '几乎每天', value: 3 }
          ]
        },
        {
          text: '您是否经常感到疲劳或缺乏精力？',
          options: [
            { text: '完全没有', value: 0 },
            { text: '有几天', value: 1 },
            { text: '一半以上的时间', value: 2 },
            { text: '几乎每天', value: 3 }
          ]
        },
        {
          text: '您是否经常感到焦虑或紧张？',
          options: [
            { text: '完全没有', value: 0 },
            { text: '有几天', value: 1 },
            { text: '一半以上的时间', value: 2 },
            { text: '几乎每天', value: 3 }
          ]
        },
        {
          text: '您是否经常难以入睡或睡眠过多？',
          options: [
            { text: '完全没有', value: 0 },
            { text: '有几天', value: 1 },
            { text: '一半以上的时间', value: 2 },
            { text: '几乎每天', value: 3 }
          ]
        }
      ],
      answers: [],
      resultIcon: 'success',
      resultTitle: '测试完成',
      resultSubtitle: '您的心理健康状况评估',
      resultDescription: '',
      suggestions: []
    }
  },
  methods: {
    nextStep() {
      this.$refs.basicInfoForm.validate((valid) => {
        if (valid) {
          this.currentStep = 1
        }
      })
    },
    prevQuestion() {
      if (this.currentQuestion > 0) {
        this.currentQuestion--
      }
    },
    nextQuestion() {
      if (this.currentQuestion < this.questions.length - 1) {
        this.currentQuestion++
      } else {
        this.calculateResult()
        this.currentStep = 2
      }
    },
    calculateResult() {
      const totalScore = this.answers.reduce((sum, score) => sum + score, 0)
      const averageScore = totalScore / this.questions.length

      if (averageScore < 1) {
        this.resultIcon = 'success'
        this.resultTitle = '心理健康状况良好'
        this.resultDescription = '您的心理健康状况良好，继续保持积极的生活态度和健康的生活方式。'
        this.suggestions = [
          '保持规律的作息时间',
          '坚持适量运动',
          '保持社交活动',
          '培养兴趣爱好'
        ]
      } else if (averageScore < 2) {
        this.resultIcon = 'warning'
        this.resultTitle = '需要注意'
        this.resultDescription = '您可能有一些轻微的情绪波动，建议关注自己的心理健康状况。'
        this.suggestions = [
          '尝试放松技巧，如深呼吸或冥想',
          '与亲友多交流',
          '保持规律的作息',
          '如持续不适，建议咨询专业人士'
        ]
      } else {
        this.resultIcon = 'error'
        this.resultTitle = '建议寻求专业帮助'
        this.resultDescription = '您的测试结果显示可能存在一些心理健康问题，建议及时寻求专业帮助。'
        this.suggestions = [
          '预约心理咨询师',
          '保持规律的作息和饮食',
          '进行适当的运动',
          '与信任的人分享感受'
        ]
      }
    },
    restartTest() {
      this.currentStep = 0
      this.currentQuestion = 0
      this.answers = []
      this.basicInfo = {
        name: '',
        age: 18,
        gender: ''
      }
    },
    downloadReport() {
      // TODO: 实现报告下载功能
      this.$message.success('报告下载功能开发中')
    }
  }
}
</script>

<style scoped>
.mental-test-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.card-header {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card-header h2 {
  margin: 0;
  text-align: center;
}

.step-content {
  padding: 20px 0;
}

.question-container {
  text-align: center;
}

.question-text {
  font-size: 18px;
  margin: 20px 0;
  color: #333;
}

.options-group {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 30px 0;
}

.option-item {
  margin: 10px 0;
  padding: 10px;
  border-radius: 4px;
  background-color: #f5f7fa;
  transition: all 0.3s;
}

.option-item:hover {
  background-color: #ecf5ff;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.result-container {
  padding: 20px;
}

.result-details {
  text-align: left;
  margin: 20px 0;
}

.suggestion-list {
  margin-top: 20px;
}

.suggestion-list h4 {
  margin-bottom: 10px;
}

.suggestion-list ul {
  list-style-type: none;
  padding: 0;
}

.suggestion-list li {
  margin: 10px 0;
  padding-left: 20px;
  position: relative;
}

.suggestion-list li:before {
  content: "•";
  position: absolute;
  left: 0;
  color: var(--el-color-primary);
}

.action-buttons {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  gap: 20px;
}
</style> 